import type { NavGroup, NavNode } from '../navigation/navTypes';
import { lookupNavigationCommand } from './navigationCommandRegistry';
import {
  isNavigationVisible,
  type PermissionSnapshot,
} from './navigationVisibility';

/**
 * Prune the sidebar the way WPF `RebuildFilteredItems` does (empty search):
 * 1. `ApplyFeatureFilter` / `PruneByFeature` — when `enabledFeatures` is null, keep
 *    the tree (fail-open). When a set is present: drop placeholders; keep a leaf
 *    with no feature (shell surfaces); otherwise `IsFeatureVisible`.
 * 2. `PruneByPermission` — `HasPermission` on each remaining leaf; drop empty groups.
 *
 * A sidebar leaf is a registry `wpfKey` or an explicit shell surface. Unregistered
 * keyless leaves are not fail-open (WPF never puts those on the tree).
 */
export function filterNavigationTree(
  nodes: readonly NavNode[],
  enabledFeatures: ReadonlySet<string> | null,
  snapshot: PermissionSnapshot
): NavNode[] {
  const kept: NavNode[] = [];
  for (const node of nodes) {
    const pruned = pruneNode(node, enabledFeatures, snapshot);
    if (pruned) kept.push(pruned);
  }
  return kept;
}

function pruneNode(
  node: NavNode,
  enabledFeatures: ReadonlySet<string> | null,
  snapshot: PermissionSnapshot
): NavNode | null {
  if (node.kind === 'placeholder') {
    return enabledFeatures == null ? node : null;
  }

  if (node.kind === 'leaf') {
    if (node.shell) {
      return isNavigationVisible(undefined, 'None', enabledFeatures, snapshot) ? node : null;
    }

    const meta = lookupNavigationCommand(node.wpfKey);
    if (!meta) return null;
    return isNavigationVisible(meta.feature, meta.permission, enabledFeatures, snapshot)
      ? node
      : null;
  }

  const children = filterNavigationTree(node.children, enabledFeatures, snapshot);
  if (children.length === 0) return null;
  const group: NavGroup = { ...node, children };
  return group;
}
